import { z } from 'zod';
import { AnticipatedCareCost } from '../../enums';
import { requiredEnum } from '../../../validation';
import {
  compareDurations,
  displayDuration,
  Duration,
  DurationSchema,
} from '../../DurationSchema';

// "Expected duration of care" — exactly one of:
// - upTo: return to work is expected within the duration ("Up to 3 months")
// - rtwNotExpectedWithin: no return to work expected inside the duration
//   ("RTW not expected within 12 months")
export const ExpectedCareDurationSchema = z
  .object(
    {
      upTo: DurationSchema.optional(),
      rtwNotExpectedWithin: DurationSchema.optional(),
    },
    'Expected duration of care is required',
  )
  .refine(
    (duration) =>
      Boolean(duration.upTo) !== Boolean(duration.rtwNotExpectedWithin),
    'Expected duration of care is required',
  );

export type ExpectedCareDuration = z.infer<typeof ExpectedCareDurationSchema>;

// The "Care Forecast" of a care document: expected care duration and
// anticipated cost against delegated authority. Captured on the Initial
// Assessment Summary for organizations with the careDocuments.careForecast
// setting on (Aviva, PPL-3295). A present forecast is complete — forms make
// the whole object required when the setting is on.
export const CareForecastSchema = z.object({
  expectedDuration: ExpectedCareDurationSchema,
  anticipatedCost: requiredEnum(
    AnticipatedCareCost,
    'Anticipated cost is required',
  ),
});

export type CareForecast = z.infer<typeof CareForecastSchema>;

// Builds the single-choice set offered on forms from the organization's
// careDocuments configuration: one "Up to ..." option per configured
// duration (ascending), plus the standalone "RTW not expected within ..."
// option when the organization offers it (null = not offered).
export function buildExpectedCareDurationOptions(
  durations: Duration[],
  rtwNotExpectedWithin: Duration | null,
): ExpectedCareDuration[] {
  return [
    ...[...durations].sort(compareDurations).map((upTo) => ({ upTo })),
    ...(rtwNotExpectedWithin ? [{ rtwNotExpectedWithin }] : []),
  ];
}

export function displayExpectedCareDuration(duration: ExpectedCareDuration) {
  if (duration.upTo) {
    return `Up to ${displayDuration(duration.upTo)}`;
  }
  if (duration.rtwNotExpectedWithin) {
    return `RTW not expected within ${displayDuration(
      duration.rtwNotExpectedWithin,
    )}`;
  }
  throw new Error(
    'Expected care duration must define upTo or rtwNotExpectedWithin',
  );
}
